import React, { useEffect, useState } from 'react';
import { PiWarningCircle } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Form, Input, Modal } from 'antd';
import Button from 'components/common/Button';
import Notice from 'components/common/Notice/Notice';
import {
  DELETE_ORGANISATION_API_ERROR_MESSAGE,
  DELETE_ORGANISATION_CONFIRM_ERROR_MESSAGE,
  DELETE_ORGANISATION_CONFIRM_LABEL,
  DELETE_ORGANISATION_CONFIRM_PLACEHOLDER,
  DELETE_ORGANISATION_CONFIRM_WORD,
  DELETE_ORGANISATION_LABEL,
  DELETE_ORGANISATION_LOADING_LABEL,
  DELETE_ORGANISATION_WARNING_PREFIX,
  DELETE_ORGANISATION_WARNING_SUFFIX,
} from 'constants/uiConstants';
import useDeleteOrganisation from 'hooks/useDeleteOrganisation';
import { OrganisationWithExtras } from 'interfaces/IOrganisations';

import { getActiveOrganisationId } from '../../redux/selectors/ActiveOrganisationSelectors';
import { clearActiveOrganisation } from '../../redux/slices/ActiveOrganisationSlice';
import { AppDispatch } from '../../redux/Store';

const deleteConfirmValidator = {
  validator: (_: unknown, value: string | undefined) => {
    if (value?.trim().toLowerCase() === DELETE_ORGANISATION_CONFIRM_WORD) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(DELETE_ORGANISATION_CONFIRM_ERROR_MESSAGE));
  },
};

interface IDeleteOrganisationModalProps {
  open: boolean;
  organisation?: OrganisationWithExtras;
  onClose: () => void;
}

const DeleteOrganisationModal = ({
  open,
  organisation,
  onClose,
}: IDeleteOrganisationModalProps) => {
  const [form] = Form.useForm();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const deleteMutation = useDeleteOrganisation();

  useEffect(() => {
    if (!open) return;

    setApiError(null);
    setIsFormValid(false);
    form.resetFields();
  }, [open, form]);

  const handleFinish = () => {
    if (!organisation) return;

    setApiError(null);

    deleteMutation.mutate(organisation.id, {
      onSuccess: () => {
        if (organisation.id === activeOrganisationId) {
          dispatch(clearActiveOrganisation());
        }
        onClose();
      },
      onError: () => {
        setApiError(DELETE_ORGANISATION_API_ERROR_MESSAGE);
      },
    });
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        variant="danger"
        className="w-full px-4 py-2 text-base font-semibold"
        disabled={!isFormValid || deleteMutation.isPending}
        onClick={() => form.submit()}
        type="button"
      >
        {deleteMutation.isPending
          ? DELETE_ORGANISATION_LOADING_LABEL
          : DELETE_ORGANISATION_LABEL}
      </Button>
    </div>
  );

  return (
    <Modal
      title={
        <span className="text-lg font-semibold text-gray-dark">
          {DELETE_ORGANISATION_LABEL}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      mask={{ closable: !deleteMutation.isPending }}
      keyboard={!deleteMutation.isPending}
      closable={!deleteMutation.isPending}
      className="!w-fit !min-w-[30vw] lg:!max-w-[30rem] sm:!max-w-[calc(100vw-2rem)]"
      classNames={{ root: '!rounded-2xl', header: '!mb-0' }}
      mousePosition={{ x: 0, y: 0 }}
      destroyOnHidden
    >
      {apiError && (
        <Alert
          message={apiError}
          type="error"
          className="mb-4"
          closable
          onClose={() => setApiError(null)}
        />
      )}

      <Notice
        icon={PiWarningCircle}
        size="md"
        className="mb-4 mt-2 max-h-[20rem] overflow-y-auto overflow-x-hidden"
      >
        {DELETE_ORGANISATION_WARNING_PREFIX}
        <span className="font-semibold">{organisation?.orgName}</span>
        {DELETE_ORGANISATION_WARNING_SUFFIX}
      </Notice>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFieldsChange={() =>
          setIsFormValid(
            form.getFieldsError().every((field) => field.errors.length === 0) &&
              !!form.getFieldValue('confirmText')
          )
        }
        requiredMark={false}
        colon={false}
        disabled={deleteMutation.isPending}
      >
        <Form.Item
          name="confirmText"
          label={
            <span className="text-label font-bold text-gray-dark">
              {DELETE_ORGANISATION_CONFIRM_LABEL}
            </span>
          }
          className="!mb-0"
          rules={[deleteConfirmValidator]}
          validateTrigger="onChange"
        >
          <Input
            placeholder={DELETE_ORGANISATION_CONFIRM_PLACEHOLDER}
            autoComplete="off"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DeleteOrganisationModal;
